import { useEffect, useMemo, useState } from 'react';
import { useAtom, atom, useAtomValue } from 'jotai';
import { atomWithQuery } from 'jotai-tanstack-query';
import { analyticsV2Service } from '@/services/analyticsV2.service';
import { queryClient } from '@/lib/query-client';
import { selectedBusinessUnitIdAtom } from '@/stores/business-unit.store';
import type {
  AnalyticsV2SeriesQueryParams,
  CoverageFunnelResult,
  DrilldownPage,
  DrilldownRequest,
  Granularity,
  ScalarMetricKey,
  ScalarMetricResult,
  SeriesMetricKey,
  SeriesMetricsBatch,
  TicketBreakdownRow,
  TicketBreakdownScope,
} from '@/types/analyticsV2';

/**
 * The brand these hooks scope to, or `null` for account-wide.
 *
 * Read from the global switcher rather than threaded in from the page, the way
 * `use-brand-health` does — analytics is a reporting view and follows the same
 * selection as the rest of the product.
 *
 * Suppressed whenever a super admin passes `overrideAccountId`: brands belong to the
 * operator's OWN account, so sending one while viewing a different account would filter
 * that account's rows by an id it does not own and return an empty dashboard with
 * nothing to say why.
 */
function useScopedBuId(overrideAccountId: number | undefined): string | null {
  const selected = useAtomValue(selectedBusinessUnitIdAtom);
  return overrideAccountId == null ? selected : null;
}

/**
 * The per-key scalar cache key, in ONE place.
 *
 * `use-consolidated-metrics` seeds this exact key so the account drawer opens from cache
 * instead of six fresh requests; when the two drifted apart the seed silently stopped
 * hitting. The brand belongs in the key, not only the request — switching brands does not
 * clear the cache, so without it the second brand is served the first brand's numbers.
 */
export function analyticsMetricQueryKey(
  accountId: number | undefined,
  fromDate: string,
  toDate: string,
  key: string,
  buId: string | null,
): readonly unknown[] {
  return ['analytics-v2', 'metric', accountId ?? 'self', fromDate, toDate, key, buId ?? 'all'] as const;
}

/**
 * Per-key scalar metric fetcher — each key resolves independently through the
 * shared queryClient cache. Built on `queryClient.fetchQuery` rather than
 * `useQueries` because only `@tanstack/query-core` is installed (no React
 * adapter). `accountId` comes from the Bearer claim server-side, not a param.
 */
export interface PerKeyMetricResult {
  key: ScalarMetricKey;
  data: ScalarMetricResult | undefined;
  isLoading: boolean;
  isError: boolean;
}

/** One level of the Ticket tab's breakdown. Each level is its own cached request, so drilling back is instant. */
export interface UseTicketBreakdownResult {
  data: TicketBreakdownRow[] | undefined;
  isLoading: boolean;
  isError: boolean;
}

export function useTicketBreakdown(
  fromDate: string,
  toDate: string,
  scope: TicketBreakdownScope,
  overrideAccountId?: number,
): UseTicketBreakdownResult {
  const buId = useScopedBuId(overrideAccountId);
  const { by, category, subcategory } = scope;
  const [state, setState] = useState<UseTicketBreakdownResult>({
    data: undefined,
    isLoading: false,
    isError: false,
  });

  useEffect(() => {
    if (!(fromDate && toDate)) return;
    let cancelled = false;
    setState((s) => ({ ...s, isLoading: true, isError: false }));
    queryClient
      .fetchQuery({
        queryKey: ['analytics-v2', 'ticket-breakdown', overrideAccountId ?? 'self', fromDate, toDate, buId ?? 'all', by, category ?? null, subcategory ?? null],
        queryFn: () =>
          analyticsV2Service.getTicketBreakdown({ fromDate, toDate, by, category, subcategory, accountId: overrideAccountId, buId }),
        staleTime: METRIC_STALE_TIME,
      })
      .then((rows) => {
        if (cancelled) return;
        setState({ data: rows, isLoading: false, isError: false });
      })
      .catch(() => {
        if (cancelled) return;
        setState({ data: undefined, isLoading: false, isError: true });
      });
    return () => {
      cancelled = true;
    };
  }, [fromDate, toDate, by, category, subcategory, overrideAccountId, buId]);

  return state;
}

/** One page of the rows behind a number. The page on screen stays while the next one loads. */
export interface UseDrilldownResult<Row> {
  /** The last page that loaded; after a failed request it is still the page before it. */
  data: DrilldownPage<Row> | undefined;
  /** No page yet. */
  isLoading: boolean;
  /** A page is on its way. */
  isFetching: boolean;
  isError: boolean;
}

export function useDrilldown<Row>(
  request: DrilldownRequest,
  fromDate: string,
  toDate: string,
  /** Each new object is fetched again, so asking for a page that failed retries it. */
  pagination: { pageIndex: number; pageSize: number },
  overrideAccountId?: number,
): UseDrilldownResult<Row> {
  const buId = useScopedBuId(overrideAccountId);
  const [state, setState] = useState<UseDrilldownResult<Row>>({
    data: undefined,
    isLoading: true,
    isFetching: true,
    isError: false,
  });

  useEffect(() => {
    if (!(fromDate && toDate)) return;
    let cancelled = false;
    const page = pagination.pageIndex + 1;
    const { pageSize } = pagination;
    setState((s) => ({ ...s, isLoading: s.data === undefined, isFetching: true, isError: false }));
    queryClient
      .fetchQuery({
        queryKey: ['analytics-v2', 'drilldown', overrideAccountId ?? 'self', fromDate, toDate, buId ?? 'all', request, page, pageSize],
        queryFn: () =>
          analyticsV2Service.getDrilldown<Row>({ fromDate, toDate, ...request, page, pageSize, accountId: overrideAccountId, buId }),
        staleTime: METRIC_STALE_TIME,
      })
      .then((data) => {
        if (cancelled) return;
        setState({ data, isLoading: false, isFetching: false, isError: false });
      })
      .catch(() => {
        if (cancelled) return;
        setState((s) => ({ ...s, isLoading: false, isFetching: false, isError: true }));
      });
    return () => {
      cancelled = true;
    };
  }, [request, fromDate, toDate, pagination, overrideAccountId, buId]);

  return state;
}

/**
 * Coverage Funnel fetcher for the super-admin Internal Analytics page. Same
 * caching pattern as `useTicketBreakdown` — one query keyed by account/from/to,
 * disabled until both dates are present, so re-rendering the tab does not
 * refetch.
 */
export interface UseFunnelResult {
  data: CoverageFunnelResult | undefined;
  isLoading: boolean;
  isError: boolean;
}

export function useFunnel(
  fromDate: string,
  toDate: string,
  overrideAccountId?: number,
): UseFunnelResult {
  const buId = useScopedBuId(overrideAccountId);
  const [state, setState] = useState<UseFunnelResult>({
    data: undefined,
    isLoading: false,
    isError: false,
  });

  useEffect(() => {
    if (!(fromDate && toDate)) return;
    let cancelled = false;
    setState((s) => ({ ...s, isLoading: true, isError: false }));
    queryClient
      .fetchQuery({
        queryKey: ['analytics-v2', 'funnel', overrideAccountId ?? 'self', fromDate, toDate, buId ?? 'all'],
        queryFn: () =>
          analyticsV2Service.getFunnel({ fromDate, toDate, accountId: overrideAccountId, buId }),
        staleTime: METRIC_STALE_TIME,
      })
      .then((result) => {
        if (cancelled) return;
        setState({ data: result, isLoading: false, isError: false });
      })
      .catch(() => {
        if (cancelled) return;
        setState({ data: undefined, isLoading: false, isError: true });
      });
    return () => {
      cancelled = true;
    };
  }, [fromDate, toDate, overrideAccountId, buId]);

  return state;
}

const METRIC_STALE_TIME = 60 * 1000;

function useSingleMetric(
  key: ScalarMetricKey,
  fromDate: string,
  toDate: string,
  overrideAccountId?: number,
): PerKeyMetricResult {
  const buId = useScopedBuId(overrideAccountId);
  const [state, setState] = useState<Omit<PerKeyMetricResult, 'key'>>({
    data: undefined,
    isLoading: false,
    isError: false,
  });

  useEffect(() => {
    if (!(fromDate && toDate)) return;
    let cancelled = false;
    setState((s) => ({ ...s, isLoading: true, isError: false }));
    queryClient
      .fetchQuery({
        queryKey: analyticsMetricQueryKey(overrideAccountId, fromDate, toDate, key, buId),
        queryFn: () =>
          analyticsV2Service.getMetrics([key], { fromDate, toDate, accountId: overrideAccountId, buId }),
        staleTime: METRIC_STALE_TIME,
      })
      .then((batch) => {
        if (cancelled) return;
        setState({ data: batch[key], isLoading: false, isError: false });
      })
      .catch(() => {
        if (cancelled) return;
        setState({ data: undefined, isLoading: false, isError: true });
      });
    return () => {
      cancelled = true;
    };
  }, [key, fromDate, toDate, overrideAccountId, buId]);

  return { key, ...state };
}

/**
 * Fan out one request per key. Callers pass a stable `keys` array (typically
 * the module-level `SCALAR_METRIC_KEYS` constant) — the hook calls
 * `useSingleMetric` once per index. The array length must be stable across
 * renders (it is — `SCALAR_METRIC_KEYS` is a frozen const).
 */
export function useMetricsPerKey(
  keys: ScalarMetricKey[],
  fromDate: string,
  toDate: string,
  overrideAccountId?: number,
): PerKeyMetricResult[] {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  return keys.map((key) => useSingleMetric(key, fromDate, toDate, overrideAccountId));
}


// ─── Series ───────────────────────────────────────────────────────────────────
// Atom-driven fetcher that refetches whenever params change. Series is fetched
// separately from scalar because it has its own granularity dimension and the
// consumer (chart card) typically asks for one metric at a time.

interface SeriesParams extends AnalyticsV2SeriesQueryParams {
  keys: SeriesMetricKey[];
}

const seriesParamsAtom = atom<SeriesParams | null>(null);

const seriesQueryAtom = atomWithQuery((get) => {
  const params = get(seriesParamsAtom);
  const keysCacheKey = params?.keys ? [...params.keys].sort().join(',') : '';
  return {
    queryKey: [
      'analytics-v2',
      'series',
      params?.accountId ?? 'self',
      params?.fromDate,
      params?.toDate,
      params?.granularity,
      keysCacheKey,
      params?.buId ?? 'all',
    ],
    queryFn: () =>
      analyticsV2Service.getSeries(params!.keys, {
        fromDate: params!.fromDate,
        toDate: params!.toDate,
        granularity: params!.granularity,
        accountId: params!.accountId,
        buId: params!.buId,
      }),
    enabled: !!(
      params?.fromDate &&
      params?.toDate &&
      params?.granularity &&
      params?.keys?.length
    ),
    staleTime: 60 * 1000,
  };
});

export function useMetricSeries(
  keys: SeriesMetricKey[],
  fromDate: string,
  toDate: string,
  granularity: Granularity,
  overrideAccountId?: number,
): { data: SeriesMetricsBatch | undefined; isLoading: boolean; isError: boolean } {
  const buId = useScopedBuId(overrideAccountId);
  const [, setParams] = useAtom(seriesParamsAtom);
  const [query] = useAtom(seriesQueryAtom);

  const keysJoined = useMemo(() => [...keys].sort().join(','), [keys]);

  useEffect(() => {
    if (fromDate && toDate && granularity && keys.length > 0) {
      setParams({ fromDate, toDate, granularity, keys, accountId: overrideAccountId, buId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, toDate, granularity, keysJoined, overrideAccountId, buId, setParams]);

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
